import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PageHeader, LoadingState, useConfirm } from '../components/ui';
import { Icon, type IconName } from '../components/icons';
import { useToast } from '../sectepe-ui/index';
import { meinProfilApi, MeinProfilUebersicht, MeinProfilPlattform, MeinKonto, ProfilStatus } from '../services/api';

const PLATTFORMEN: Array<{ id: MeinProfilPlattform; name: string; icon: IconName; hinweis: string }> = [
  { id: 'linkedin', name: 'LinkedIn', icon: 'linkedin', hinweis: 'Dein persönliches LinkedIn-Profil.' },
  { id: 'x', name: 'X', icon: 'send', hinweis: 'Dein persönliches X-Konto (ehemals Twitter).' },
  { id: 'instagram', name: 'Instagram', icon: 'image', hinweis: 'Dein Instagram-Profikonto (Business oder Creator).' },
];
const NAME: Record<string, string> = { linkedin: 'LinkedIn', x: 'X', instagram: 'Instagram' };

const STATUS_TEXT: Record<ProfilStatus, string> = {
  aktiv: 'Verbunden',
  laeuftAb: 'Läuft bald ab',
  abgelaufen: 'Abgelaufen – bitte neu verbinden',
  getrennt: 'Getrennt',
};
const STATUS_BADGE: Record<ProfilStatus, string> = {
  aktiv: 'badge-success',
  laeuftAb: 'badge-warning',
  abgelaufen: 'badge-warning',
  getrennt: 'badge-neutral',
};

const datum = (iso: string | null | undefined) =>
  (iso ? new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '–');

/** Nur echte https-Links anzeigen (Antwort der Plattform). */
const sichererLink = (url: string | null | undefined) => (url && /^https:\/\/\S+$/i.test(url) ? url : null);

/**
 * "Mein Profil verbinden": jede angemeldete Person -- ohne besonderes
 * Recht -- verbindet hier ihr eigenes LinkedIn-, X- und Instagram-Konto.
 * Marketing veroeffentlicht darueber freigegebene Beitraege in ihrem Namen;
 * die Liste unten zeigt, was zuletzt in ihrem Namen erschienen ist.
 * Facebook fehlt: dort gibt es nur Seiten (Firmenkonten).
 */
const MeinProfilPage: React.FC = () => {
  const [daten, setDaten] = useState<MeinProfilUebersicht | null>(null);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState('');
  const [busy, setBusy] = useState<MeinProfilPlattform | null>(null);
  const { push } = useToast();
  const { confirm } = useConfirm();
  const location = useLocation();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      setLaden(true);
      setFehler('');
      setDaten((await meinProfilApi.get()) ?? null);
    } catch (err: any) {
      setFehler(err?.response?.data?.error || 'Deine Verbindungen konnten nicht geladen werden.');
    } finally {
      setLaden(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Rueckkehr aus der Anmeldung (LinkedIn bzw. Instagram/X): melden, URL aufraeumen.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const linkedInOk = params.get('linkedin_connected') === 'true';
    const socialOk = params.get('social_connected');
    const fehlerText = params.get('linkedin_error') || params.get('social_error');
    if (!linkedInOk && !socialOk && !fehlerText) return;
    if (linkedInOk || socialOk) {
      push({ message: `${linkedInOk ? 'LinkedIn' : NAME[socialOk || ''] || 'Konto'} verbunden.`, severity: 'success' });
      load();
    }
    if (fehlerText) push({ message: fehlerText, severity: 'error', duration: 0 });
    for (const k of ['linkedin_connected', 'linkedin_error', 'social_connected', 'social_error']) params.delete(k);
    const rest = params.toString();
    navigate(`${location.pathname}${rest ? `?${rest}` : ''}`, { replace: true });
  }, [location.search, location.pathname, navigate, push, load]);

  const verbinden = async (plattform: MeinProfilPlattform) => {
    try {
      setBusy(plattform);
      const { authUrl } = await meinProfilApi.initiateOAuth(plattform);
      window.location.href = authUrl;
    } catch (err: any) {
      push({ message: err?.response?.data?.error || `Die Anmeldung bei ${NAME[plattform]} konnte nicht gestartet werden.`, severity: 'error' });
      setBusy(null);
    }
  };

  const trennen = async (plattform: MeinProfilPlattform) => {
    const ok = await confirm({
      title: `${NAME[plattform]}-Verbindung trennen?`,
      message: 'Danach kann niemand mehr über Intermal in deinem Namen veröffentlichen. Geplante Beiträge auf diesem Profil bleiben liegen, bis du neu verbindest.',
      confirmLabel: 'Trennen',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      setBusy(plattform);
      await meinProfilApi.disconnect(plattform);
      push({ message: `${NAME[plattform]}-Verbindung getrennt.`, severity: 'success' });
      await load();
    } catch (err: any) {
      push({ message: err?.response?.data?.error || 'Die Verbindung konnte nicht getrennt werden.', severity: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const kontoZeile = (k: MeinKonto) => (
    <li key={k.id} className="linkedin-card__conn">
      <div className="linkedin-card__conn-text">
        <strong>{k.kontoName}</strong>
        <span className="text-xs text-muted">
          Verbunden seit {datum(k.verbundenSeit)}
          {k.tokenExpiresAt && k.status !== 'aktiv' && <> · Zugang gültig bis {datum(k.tokenExpiresAt)}</>}
        </span>
      </div>
      <span className={`badge ${STATUS_BADGE[k.status]}`}>{STATUS_TEXT[k.status]}</span>
    </li>
  );

  return (
    <div className="page-container">
      <PageHeader
        eyebrow="Mein Konto"
        title="Mein Profil verbinden"
        subtitle="Verbinde dein persönliches LinkedIn-, X- oder Instagram-Konto, damit freigegebene Beiträge auch über dein Profil erscheinen können."
      />

      <div className="info-panel info-panel-primary" role="note" style={{ marginBottom: 'var(--space-5)' }}>
        <strong>Was passiert mit der Verbindung?</strong>
        <p className="text-sm" style={{ margin: '6px 0 0' }}>
          Marketing kann in deinem Namen Beiträge veröffentlichen, die vorher freigegeben wurden. Du kannst die
          Verbindung jederzeit trennen – hier oder direkt bei der Plattform (Einstellungen → verbundene Apps).
          Unten siehst du, was zuletzt in deinem Namen veröffentlicht wurde.
        </p>
      </div>

      {fehler && <div className="alert alert-error" role="alert">{fehler}</div>}

      {laden && !daten ? (
        <LoadingState rows={3} label="Verbindungen werden geladen" />
      ) : daten && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '12px', marginBottom: 'var(--space-6)' }}>
            {PLATTFORMEN.map((p) => {
              const info = daten.plattformen?.[p.id];
              const konten = info?.konten ?? [];
              const verbunden = konten.some((k) => k.status === 'aktiv' || k.status === 'laeuftAb');
              return (
                <section key={p.id} className="card" aria-label={p.name} data-testid={`mein-profil-${p.id}`} style={{ margin: 0 }}>
                  <div className="linkedin-card__head">
                    <span className="linkedin-card__icon" aria-hidden="true"><Icon name={p.icon} size={18} /></span>
                    <div className="linkedin-card__title">
                      <h2 className="card-title">{p.name}</h2>
                      {!info?.configured ? (
                        <span className="badge badge-neutral">Nicht eingerichtet</span>
                      ) : verbunden ? (
                        <span className="badge badge-success">Verbunden</span>
                      ) : (
                        <span className="badge badge-warning">Nicht verbunden</span>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-secondary">{p.hinweis}</p>
                  {!info?.configured ? (
                    <p className="text-sm text-secondary">
                      Die Anbindung ist noch nicht eingerichtet. Eine Inhaberin bzw. ein Inhaber trägt die Zugangsdaten unter Verwaltung → Optionen ein.
                    </p>
                  ) : (
                    <>
                      {konten.length > 0 && <ul className="linkedin-card__list">{konten.map(kontoZeile)}</ul>}
                      <div className="btn-group" style={{ marginTop: '8px' }}>
                        <button type="button" className="btn btn-primary btn-sm" onClick={() => verbinden(p.id)} disabled={busy !== null}>
                          {konten.length > 0 ? 'Neu verbinden' : 'Verbinden'}
                        </button>
                        {konten.length > 0 && (
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => trennen(p.id)} disabled={busy !== null}>
                            Trennen
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </section>
              );
            })}
          </div>

          <section className="card" aria-label="Zuletzt in deinem Namen veröffentlicht">
            <h2 className="card-title">Zuletzt in deinem Namen veröffentlicht</h2>
            {(daten.veroeffentlichungen ?? []).length === 0 ? (
              <p className="text-sm text-secondary">Über deine verbundenen Profile wurde noch nichts veröffentlicht.</p>
            ) : (
              <table className="table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th scope="col">Datum</th>
                    <th scope="col">Plattform</th>
                    <th scope="col">Profil</th>
                    <th scope="col">Veröffentlicht von</th>
                    <th scope="col">Beitrag</th>
                  </tr>
                </thead>
                <tbody>
                  {daten.veroeffentlichungen.map((v, i) => {
                    const link = sichererLink(v.url);
                    return (
                      <tr key={`${v.plattform}-${v.datum}-${i}`}>
                        <td>{datum(v.datum)}</td>
                        <td>{NAME[v.plattform] || v.plattform}</td>
                        <td>{v.kontoName}</td>
                        <td>{v.durch ? (v.durch.selbst ? 'Du selbst' : v.durch.name) : 'Unbekannt'}</td>
                        <td>{link ? <a href={link} target="_blank" rel="noopener noreferrer">Ansehen</a> : '–'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default MeinProfilPage;
